// Schedule › To schedule › Find a lead (BL-092), Blueprint's (BL-189): every lead, searched by name,
// phone, email or what they asked, open, booked or closed, or only those from an ad, each a RowList row with
// its state at the end and the ad click it came from folded under a Link (KeyValue). What it does is
// use-lead-search.ts's, as Riverstone's LeadSearch.tsx.
import { useState } from 'react';
import { Checkbox, KeyValue, Link, RowList, SelectField, TextField, type Row } from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { clickLine, clickRows, type AdClick } from '../shared/ad-click-model.ts';
import { searchCount, type SearchStatus } from './lead-search-model.ts';
import { leadLine, leadName } from './LeadsPart.tsx';
import { SEARCH_STATUSES, STATUS_WORDS, useLeadSearch } from './use-lead-search.ts';

export function FindLead({ timeZone }: { timeZone: string }) {
  const s = useLeadSearch();
  return (
    <Section title="Find a lead">
      <div className="bp-tobook-find" role="search">
        <TextField
          type="search"
          label="Name, phone, email or what they asked"
          hideLabel
          placeholder="Name, phone, email or what they asked"
          maxLength={100}
          value={s.q}
          onChange={s.setQ}
        />
        <div className="bp-tobook-find-by">
          <SelectField
            label="Which leads"
            hideLabel
            value={s.status}
            onChange={(v) => s.setStatus(v as SearchStatus)}
            options={SEARCH_STATUSES}
          />
          <Checkbox isSelected={s.fromAds} onChange={s.setFromAds}>
            From an ad
          </Checkbox>
        </div>
      </div>
      {s.error && <p className="bp-tobook-bad">{s.error}</p>}
      {s.asked && !s.found && !s.error && <p className="bp-tobook-sub">Searching…</p>}
      {s.found && (
        <>
          <p className="bp-tobook-sub" role="status">
            {searchCount(s.found.total, s.found.leads.length)}
          </p>
          {s.found.leads.length > 0 && (
            <RowList
              label="Leads found"
              rows={s.found.leads.map((l): Row => ({
                id: l.id,
                name: leadName(l),
                end: { word: STATUS_WORDS[l.status], tone: 'muted' },
                lines: [
                  { text: leadLine(l, timeZone) },
                  { text: l.summary, tone: 'ink' },
                  ...(l.click ? [{ part: <AdClickFold click={l.click} timeZone={timeZone} /> }] : []),
                ],
              }))}
            />
          )}
          {s.found.next && (
            <p className="bp-tobook-more">
              <Link isDisabled={s.more} onPress={() => void s.showMore()}>
                {s.more ? 'Getting more…' : 'Show more'}
              </Link>
            </p>
          )}
        </>
      )}
    </Section>
  );
}

/** The ad click a lead came from, closed to its line until opened. */
function AdClickFold({ click, timeZone }: { click: AdClick; timeZone: string }) {
  const [open, setOpen] = useState(false);
  const when = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  return (
    <>
      <Link
        onPress={() => setOpen((o) => !o)}
        label={`From an ad: ${clickLine(click)}. ${open ? 'Hide' : 'Show'} the click`}
      >
        From an ad: {clickLine(click)}
      </Link>
      {open && (
        <KeyValue
          density="tight"
          label="The ad click"
          items={clickRows(click, when).map(([k, v]) => ({ label: k, value: v }))}
        />
      )}
    </>
  );
}
